import React from 'react';
import { PageHeader, Card, Skeleton, SkeletonButton, SkeletonInput } from '@parsina/framework';
import { C } from '../../constants/theme';
import Panel from '../../components/common/Panel.jsx';
import {
  SkeletonPage, MobileHeaderSkeleton, SectionNavSkeleton, SegmentedSkeleton,
  TitledRowsSkeleton, InfoRowsSkeleton,
} from '../../components/common/skeletons.jsx';
import { MobileSectionTitle } from '../../components/mobile/MobileKit.jsx';
import { PROFILE_SECTION_IDS } from './Profile.shared.js';

// پروفایل — the loading shape of the profile screen, per section.
//
// The page title is REAL (it is a translated string, known before the data),
// and so is nothing else: the section strip and the section body shimmer in
// the shape of whichever `?tab=` the URL names, so a reload on the presence
// section draws shift rows, not identity fields.
//   identity    the three-field form + bio textarea (web: in a Panel with a
//               ذخیره button; mobile: in a Card)
//   clinics     membership rows + the «پیوستن با کد» form
//   presence    weekly shift rows
//   appearance  the theme preset grid + two segmented controls

// ── section bodies ──────────────────────────────────────────────────────────

const IdentityFields = ({ mobile }) => (
  <>
    <div style={mobile
      ? { display: 'flex', flexDirection: 'column', gap: '0.75rem' }
      : { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}
    >
      <SkeletonInput labelWidth={72} />
      <SkeletonInput labelWidth={88} />
      <SkeletonInput labelWidth={64} />
    </div>
    <div style={{ marginTop: mobile ? '0.75rem' : 14 }}>
      <SkeletonInput labelWidth={56} multiline />
    </div>
  </>
);

const PresetGrid = ({ mobile }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: mobile ? '0.5rem' : 10 }}>
    {Array.from({ length: 5 }, (_, i) => (
      <Skeleton key={i} width={mobile ? 96 : 120} height={mobile ? 44 : 64} radius={12} />
    ))}
  </div>
);

const WEB_SECTION = {
  identity: () => (
    <Panel title={<Skeleton width={96} height={14} />} action={<SkeletonButton width={96} />}>
      <IdentityFields />
    </Panel>
  ),
  clinics: () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel title={<Skeleton width={110} height={14} />}>
        <TitledRowsSkeleton count={2} gap={20} />
      </Panel>
      <Panel title={<Skeleton width={120} height={14} />}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <SkeletonInput labelWidth={96} style={{ flex: 1 }} />
          <SkeletonButton width={112} />
        </div>
      </Panel>
    </div>
  ),
  presence: () => (
    <Panel title={<Skeleton width={110} height={14} />}>
      <TitledRowsSkeleton count={4} gap={16} />
    </Panel>
  ),
  appearance: () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel title={<Skeleton width={72} height={14} />}><PresetGrid /></Panel>
      <Panel title={<Skeleton width={96} height={14} />}><SegmentedSkeleton count={3} /></Panel>
      <Panel title={<Skeleton width={64} height={14} />}><SegmentedSkeleton count={2} /></Panel>
    </div>
  ),
};

const MOBILE_SECTION = {
  identity: () => (
    <Card style={{ padding: '0.875rem' }}>
      <IdentityFields mobile />
    </Card>
  ),
  clinics: () => (
    <>
      <MobileSectionTitle><Skeleton width={96} height={12} /></MobileSectionTitle>
      {[0, 1].map((i) => (
        <Card key={i} style={{ padding: '0.875rem', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <Skeleton width="50%" height={14} />
              <Skeleton width="30%" height={11} />
            </div>
            <Skeleton width={56} height={22} radius={999} />
          </div>
        </Card>
      ))}
      <MobileSectionTitle style={{ marginTop: '0.875rem' }}><Skeleton width={120} height={12} /></MobileSectionTitle>
      <Card style={{ padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <SkeletonInput labelWidth={96} />
        <SkeletonButton width="100%" />
      </Card>
    </>
  ),
  presence: () => (
    <>
      <MobileSectionTitle><Skeleton width={96} height={12} /></MobileSectionTitle>
      <Card style={{ padding: '0.25rem 0.875rem' }}>
        <InfoRowsSkeleton count={4} />
      </Card>
    </>
  ),
  appearance: () => (
    <>
      <MobileSectionTitle><Skeleton width={72} height={12} /></MobileSectionTitle>
      <Card style={{ padding: '0.875rem' }}><PresetGrid mobile /></Card>
      <MobileSectionTitle style={{ marginTop: '0.875rem' }}><Skeleton width={96} height={12} /></MobileSectionTitle>
      <Card style={{ padding: '0.875rem' }}><SegmentedSkeleton count={3} /></Card>
      <MobileSectionTitle style={{ marginTop: '0.875rem' }}><Skeleton width={64} height={12} /></MobileSectionTitle>
      <Card style={{ padding: '0.875rem' }}><SegmentedSkeleton count={2} /></Card>
    </>
  ),
};

// ── the pages ───────────────────────────────────────────────────────────────

export const ProfileSkeletonWeb = ({ section = 'identity', label, title, sub }) => {
  const Body = WEB_SECTION[section] || WEB_SECTION.identity;
  return (
    <SkeletonPage
      label={label}
      data-testid="profile-content"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}
    >
      <PageHeader title={title} sub={sub} />
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '20px 32px 40px' }}>
        <div style={{ marginBottom: 16 }}>
          <SegmentedSkeleton count={PROFILE_SECTION_IDS.length} />
        </div>
        <Body />
      </div>
    </SkeletonPage>
  );
};

export const ProfileSkeletonMobile = ({ section = 'identity', label }) => {
  const Body = MOBILE_SECTION[section] || MOBILE_SECTION.identity;
  return (
    <SkeletonPage
      label={label}
      data-testid="profile-content"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <MobileHeaderSkeleton greeting subtitle={false} />
      <SectionNavSkeleton count={PROFILE_SECTION_IDS.length} />
      <div style={{ padding: '0.875rem 0.75rem 1.5rem' }}>
        <Body />
      </div>
    </SkeletonPage>
  );
};
